import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, Flame, Shield, Scale, BadgeCheck, TrendingUp,
  Building2, Car, Home as HomeIcon, Heart, PawPrint, Plane, Key, Sparkles,
  LifeBuoy, PiggyBank, ShieldCheck, HeartHandshake, Newspaper,
} from "lucide-react";
import Layout from "../components/Layout";
import { WhatsAppIcon } from "../components/icons/WhatsAppIcon";
import SolicitudCotizacionForm from "../components/SolicitudCotizacionForm";
import { PressVideo } from "../components/PressVideo";
import { useBranding } from "../hooks/useBranding";

const PILARES = [
  {
    num: "01",
    tag: "Mejor producto",
    titleParts: ["El mejor", { italic: "precio y cobertura" }, "del mercado."],
    icon: Sparkles,
  },
  {
    num: "02",
    tag: "Mejoramos tu póliza",
    titleParts: ["Envíanos tu póliza,", { italic: "siempre" }, "la mejoramos."],
    icon: Shield,
    highlighted: true,
    cta: { label: "Enviar mi póliza", to: "#solicitud-cotizacion" },
  },
  {
    num: "03",
    tag: "Postventa especializada",
    titleParts: ["Acompañamiento real,", { italic: "resolvemos rápido." }],
    icon: LifeBuoy,
  },
];

const SEGUROS_EMPRESAS = [
  { icon: Flame, name: "Incendio", tag: "Obligatorio por ley" },
  { icon: Shield, name: "Incendio + Sismo", tag: "Multi-riesgo ampliado" },
  { icon: Scale, name: "Responsabilidad Civil D&O", tag: "Administrador y comité" },
  { icon: BadgeCheck, name: "Vida OS10 Vigilantes", tag: "Para guardias" },
  { icon: Shield, name: "Robo áreas comunes", tag: "Bodegas y espacios" },
  { icon: Building2, name: "Riesgos naturales", tag: "Aluviones, vientos" },
  { icon: TrendingUp, name: "Desempleo GGCC", tag: "Cubre cuotas comunes" },
];

const SEGUROS_PERSONAS = [
  { icon: Flame, name: "Incendio general", tag: "Obligatorio" },
  { icon: Flame, name: "Incendio hipotecario", tag: "Reemplaza el bancario" },
  { icon: Key, name: "Garantía de arriendo", tag: "Sin aval" },
  { icon: HomeIcon, name: "Hogar", tag: "Contenido y continente" },
  { icon: Car, name: "Auto modelo flota", tag: "Mejor precio" },
  { icon: Heart, name: "Salud complementario", tag: "Ambulatorio y hospitalario" },
  { icon: PawPrint, name: "Mascotas", tag: "Veterinario incluido" },
  { icon: Plane, name: "Viaje", tag: "Cobertura global" },
];

function renderTitle(parts) {
  return parts.map((p, i) => {
    if (typeof p === "string") return <React.Fragment key={i}>{p} </React.Fragment>;
    return (
      <span key={i} className="serif" style={{ fontSize: "1.05em", marginRight: 6 }}>
        {p.italic}{" "}
      </span>
    );
  });
}

export default function Home() {
  // Scroll suave al anchor cuando se llega con hash (ej. /#solicitud-cotizacion)
  React.useEffect(() => {
    const h = window.location.hash;
    if (h) {
      const id = h.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 80);
    }
  }, []);

  return (
    <Layout>
      <PartnerBanner />
      {/* HERO — refined */}
      <section className="relative grain overflow-hidden" style={{ background: "linear-gradient(180deg, var(--bg-cream) 0%, #FFFFFF 65%)" }}>
        {/* Decorative architectural blur */}
        <div aria-hidden className="absolute -top-32 -right-32 w-[560px] h-[560px] rounded-full opacity-[0.08] blur-3xl" style={{ background: "var(--brand-accent)" }}/>
        <div aria-hidden className="absolute -bottom-40 -left-40 w-[640px] h-[640px] rounded-full opacity-[0.07] blur-3xl" style={{ background: "var(--brand-navy)" }}/>

        <div className="container-x relative pt-4 pb-5 md:pt-6 md:pb-7">
          <div className="flex flex-wrap md:flex-nowrap items-center gap-x-2 gap-y-2 mb-6 md:mb-8" data-testid="hero-activos">
            <span className="text-[12px] font-bold whitespace-nowrap" style={{ color: "var(--text-secondary)", fontFamily: "Manrope" }}>
              Aseguramos todo tipo de activos inmobiliarios:
            </span>
            {["Comunidades", "Proyectos inmobiliarios nuevos", "Multifamily", "Bodegas", "Placas comerciales"].map((a) => (
              <span key={a} className="text-[10.5px] md:text-[11px] font-semibold px-2.5 py-1 rounded-full whitespace-nowrap" style={{ background: "rgba(255,255,255,0.7)", border: "1px solid var(--border-medium)", color: "var(--text-secondary)" }}>
                {a}
              </span>
            ))}
            <span className="text-[11px] font-semibold serif whitespace-nowrap" style={{ color: "var(--brand-accent)" }}>y más.</span>
          </div>

          <div className="grid md:grid-cols-12 gap-10 md:gap-12 lg:gap-16 items-center">
            <div className="md:col-span-7 fade-in-up">
              <h1 className="text-[34px] sm:text-[40px] md:text-[56px] lg:text-[68px] leading-[1.05] md:leading-[1.02] font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>
                Los mejores seguros para tu comunidad, con <span className="serif" style={{ color: "var(--brand-accent)", fontSize: "1.04em" }}>especialistas de verdad</span>.
              </h1>

              <div className="mt-7 max-w-xl" data-testid="hero-prefieren">
                <div className="text-[10.5px] uppercase tracking-[0.22em] font-bold mb-1.5" style={{ color: "var(--brand-accent)" }}>Por qué nos prefieren</div>
                <p className="text-lg md:text-xl font-bold" style={{ color: "var(--text-primary)", fontFamily: "Manrope", lineHeight: 1.35 }}>
                  Ahorro real, mejores coberturas y un <span className="serif font-normal" style={{ color: "var(--brand-accent)", fontSize: "1.04em" }}>servicio 100% presente.</span>
                </p>
                <div className="mt-4 space-y-2.5">
                  {[
                    { icon: PiggyBank, tag: "Ahorro", title: "Generamos ahorro considerable.", text: "Bajamos la prima de tu comunidad o activo inmobiliario, sin sacrificar protección." },
                    { icon: ShieldCheck, tag: "Coberturas", title: "Mejoramos las coberturas.", text: "Revisamos tu póliza y la dejamos mejor: más protección por el mismo o menor costo." },
                    { icon: HeartHandshake, tag: "Lo más importante", title: "100% postventa.", text: "Acompañamiento real y gestión completa del siniestro, de principio a fin.", highlighted: true },
                  ].map((item, i) => {
                    const Icon = item.icon;
                    return (
                      <div key={i} className="flex items-start gap-3 rounded-xl px-3.5 py-2.5" style={{
                        background: item.highlighted ? "rgba(238,81,71,0.06)" : "rgba(255,255,255,0.65)",
                        border: item.highlighted ? "1px solid rgba(238,81,71,0.35)" : "1px solid var(--border-subtle)",
                      }} data-testid={`hero-prefieren-${i}`}>
                        <span className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: item.highlighted ? "var(--brand-accent)" : "rgba(10,31,54,0.06)" }}>
                          <Icon size={16} color={item.highlighted ? "white" : "var(--brand-navy)"} strokeWidth={1.9}/>
                        </span>
                        <div className="leading-snug">
                          <div className="text-[9.5px] uppercase tracking-[0.18em] font-bold" style={{ color: item.highlighted ? "var(--brand-accent)" : "var(--text-muted)" }}>{item.tag}</div>
                          <div className="text-[13.5px]">
                            <span className="font-extrabold" style={{ color: "var(--text-primary)", fontFamily: "Manrope" }}>{item.title}</span>{" "}
                            <span style={{ color: "var(--text-muted)" }}>{item.text}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-8 md:mt-10 flex flex-wrap gap-3">
                <a href="#solicitud-cotizacion" className="btn-primary w-full sm:w-auto justify-center" data-testid="hero-cta-contacto">
                  Completa el formulario y te contactamos <ArrowUpRight size={16}/>
                </a>
                <a href="#solicitud-cotizacion" className="btn-outline w-full sm:w-auto justify-center" data-testid="hero-cta-mejorar">
                  Mejorar mi póliza
                </a>
              </div>
            </div>

            {/* Hero visual — architectural image with overlay */}
            <div className="md:col-span-5 fade-in-up stagger-2">
              <div className="relative">
                <div className="absolute -top-6 -left-6 w-32 h-32 rounded-2xl opacity-90 hidden md:block" style={{ background: "var(--brand-accent)" }}></div>
                <div className="relative overflow-hidden rounded-2xl border" style={{ borderColor: "var(--border-medium)", boxShadow: "var(--shadow-card)" }}>
                  <img
                    src="https://customer-assets.emergentagent.com/job_cotiza-ahora/artifacts/s79p7liw_ChatGPT%20Image%2013%20may%202026%2C%2020_23_53.png"
                    alt="Familia en barrio residencial moderno con cordillera al fondo"
                    className="w-full h-[340px] sm:h-[420px] md:h-[520px] object-cover"
                  />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, transparent 50%, rgba(10, 31, 54, 0.85) 100%)" }}></div>
                  <div className="absolute bottom-0 left-0 right-0 p-7 text-white">
                    <div className="text-[10px] uppercase tracking-[0.22em] opacity-80 mb-2 font-bold" style={{ fontFamily: "Manrope" }}>
                      El estándar
                    </div>
                    <div className="text-2xl font-extrabold leading-tight" style={{ fontFamily: "Manrope" }}>
                      Cobertura completa.<br/>
                      <span className="serif font-normal" style={{ fontSize: "1.05em" }}>Acompañamiento real.</span>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="flex items-center gap-3 pl-3 pr-4 py-2 rounded-full mt-5 justify-center"
                style={{
                  background: "rgba(238,81,71,0.06)",
                  border: "1px solid rgba(238,81,71,0.22)",
                }}
                data-testid="hero-savings-claim"
              >
                <span
                  className="inline-flex items-center justify-center w-7 h-7 rounded-full flex-shrink-0"
                  style={{ background: "var(--brand-accent)" }}
                >
                  <TrendingUp size={15} color="white" strokeWidth={2.4}/>
                </span>
                <span className="text-[11px] sm:text-[12.5px] leading-tight" style={{ color: "var(--text-secondary)", fontFamily: "Manrope" }}>
                  <span className="font-extrabold" style={{ color: "var(--text-primary)" }}>Les generamos $ millones de pesos de ahorro</span>
                  <span className="opacity-80"> a las comunidades</span>
                  <span className="mx-1.5 opacity-40">·</span>
                  <span className="font-extrabold whitespace-nowrap" style={{ color: "var(--brand-accent)" }}>#AhorraEnComunidad</span>
                </span>
              </div>
            </div>
          </div>

          {/* Trust strip — aseguradoras (full width, single line on desktop) */}
          <div className="mt-6 md:mt-8 flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-8" data-testid="hero-trust-strip">
            <p className="text-[12px] md:text-[12.5px] leading-snug flex-shrink-0" style={{ color: "var(--text-muted)", fontFamily: "Manrope" }}>
              Contamos con las <span className="font-semibold" style={{ color: "var(--text-secondary)" }}>mejores condiciones del mercado</span> de comunidades en:
            </p>
            <div className="flex flex-wrap lg:flex-nowrap items-center gap-x-5 gap-y-2.5 md:gap-x-7 lg:gap-x-8">
              <img src="https://customer-assets.emergentagent.com/job_cotiza-ahora/artifacts/hvoi8473_Logo-mapfre-fondo-transparente.png" alt="Mapfre" className="h-5 md:h-[22px] w-auto object-contain opacity-90 hover:opacity-100 transition" draggable={false} />
              <img src="https://customer-assets.emergentagent.com/job_cotiza-ahora/artifacts/3qv8alht_logo-bci.png" alt="BCI Seguros" className="h-5 md:h-[22px] w-auto object-contain opacity-90 hover:opacity-100 transition" draggable={false} />
              <img src="https://customer-assets.emergentagent.com/job_cotiza-ahora/artifacts/zunt64oe_Seguros_SURA_Logo.svg.png" alt="SURA Seguros" className="h-5 md:h-[22px] w-auto object-contain opacity-90 hover:opacity-100 transition" draggable={false} />
              <img src="https://customer-assets.emergentagent.com/job_cotiza-ahora/artifacts/uwk5uky4_Zurich_Insurance_Group_Logo_Horizontal.svg.png" alt="Zurich" className="h-5 md:h-[22px] w-auto object-contain opacity-90 hover:opacity-100 transition" draggable={false} />
              <img src="https://customer-assets.emergentagent.com/job_cotiza-ahora/artifacts/gwdap9s6_03-clientes-hdi.png" alt="HDI Seguros" className="h-7 md:h-8 w-auto object-contain opacity-90 hover:opacity-100 transition" draggable={false} />
            </div>
          </div>
        </div>
      </section>

      {/* EN PRENSA — videos embebidos */}
      <section className="py-7 md:py-9" id="en-prensa" style={{ background: "var(--bg-soft)" }} data-testid="prensa-section">
        <div className="container-x">
          <div className="mb-8 md:mb-10">
            <div className="eyebrow mb-3 flex items-center gap-2"><Newspaper size={13}/> Prensa</div>
            <h2 className="text-3xl md:text-4xl lg:text-[44px] font-extrabold tracking-tight" style={{ fontFamily: "Manrope", lineHeight: 1.08 }}>
              Mi Comunidad Segura <span className="serif" style={{ color: "var(--brand-accent)", fontSize: "1.04em" }}>en prensa.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-5 md:gap-7">
            <PressVideo id="vadjoS3HIe0" medio="Canal 13" title="¿Qué cubre mi seguro?: Las dudas tras el aluvión" />
            <PressVideo id="0TH_Hs4szfw" medio="Radio ADN" title="Hablemos de Seguros: Comunidades, Casas, locales y autos" />
          </div>
        </div>
      </section>

      {/* 3 PILARES — refined */}
      <section className="pt-8 pb-12 md:pt-10 md:pb-20" id="por-que-nosotros">
        <div className="container-x">
          <div className="mb-10 md:mb-14">
            <div className="eyebrow mb-4">Por qué nosotros</div>
            <h2 className="text-4xl md:text-5xl lg:text-[56px] font-extrabold tracking-tight whitespace-normal md:whitespace-nowrap" style={{ fontFamily: "Manrope", lineHeight: 1.05 }}>
              Tres razones para <span className="serif" style={{ color: "var(--brand-accent)" }}>no buscar más.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
            {PILARES.map((p, i) => {
              const Icon = p.icon;
              return (
                <article
                  key={i}
                  className={`group relative p-6 md:p-10 rounded-2xl border transition-all duration-500 md:flex md:flex-col fade-in-up stagger-${i+1}`}
                  style={{
                    borderColor: p.highlighted ? "var(--brand-accent)" : "var(--border-subtle)",
                    background: p.highlighted ? "linear-gradient(180deg, #FFFFFF 0%, rgba(201, 102, 63, 0.04) 100%)" : "white",
                    boxShadow: p.highlighted ? "0 0 0 1px var(--brand-accent) inset, var(--shadow-card)" : "var(--shadow-soft)",
                  }}
                  data-testid={`pilar-${i}`}
                  onMouseEnter={(e) => { e.currentTarget.style.boxShadow = p.highlighted ? "0 0 0 1px var(--brand-accent) inset, var(--shadow-card-hover)" : "var(--shadow-card-hover)"; e.currentTarget.style.transform = "translateY(-3px)"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.boxShadow = p.highlighted ? "0 0 0 1px var(--brand-accent) inset, var(--shadow-card)" : "var(--shadow-soft)"; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  <div className="flex items-start justify-between mb-6 md:mb-10">
                    <div
                      className="text-[64px] md:text-[96px] font-extrabold tracking-tight leading-none"
                      style={{
                        fontFamily: "Manrope",
                        color: p.highlighted ? "var(--brand-accent)" : "var(--brand-navy)",
                        opacity: p.highlighted ? 0.95 : 0.85,
                        marginTop: "-12px",
                      }}
                    >
                      {p.num}
                    </div>
                    <div className="w-12 h-12 rounded-xl flex items-center justify-center transition-transform duration-500 group-hover:scale-110" style={{ background: p.highlighted ? "var(--brand-accent)" : "rgba(10, 31, 54, 0.06)" }}>
                      <Icon size={20} color={p.highlighted ? "white" : "var(--brand-navy)"} strokeWidth={1.75}/>
                    </div>
                  </div>

                  <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-3" style={{ color: p.highlighted ? "var(--brand-accent)" : "var(--text-secondary)" }}>
                    {p.tag}
                  </div>
                  <h3 className="text-xl md:text-[28px] font-extrabold tracking-tight leading-[1.2] md:leading-[1.15] md:flex-1" style={{ fontFamily: "Manrope" }}>
                    {renderTitle(p.titleParts)}
                  </h3>

                  {p.cta && (
                    <a href={p.cta.to} className="btn-accent mt-6 md:mt-8 self-start" style={{ padding: "0.7rem 1.15rem", fontSize: 13.5 }}>
                      {p.cta.label} <ArrowRight size={14}/>
                    </a>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* SOLICITUD DE COTIZACIÓN — form principal */}
      <SolicitudCotizacionForm />

      {/* PRODUCTOS — refined */}
      <section className="py-16 md:py-20" id="productos" style={{ background: "var(--bg-soft)" }}>
        <div className="container-x">
          <div className="text-center mb-10 md:mb-14 max-w-2xl mx-auto">
            <h2 className="text-4xl md:text-5xl lg:text-[56px] font-extrabold tracking-tight whitespace-normal md:whitespace-nowrap" style={{ fontFamily: "Manrope", lineHeight: 1.05 }}>
              Todos los seguros <span className="serif" style={{ color: "var(--brand-accent)" }}>que necesitas.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-5 lg:gap-6">
            <ProductCard
              kicker="Para administradores y comités"
              title="Comunidad o edificio"
              icon={Building2}
              accent="navy"
              items={SEGUROS_EMPRESAS}
              testid="card-empresas"
              image="https://customer-assets.emergentagent.com/job_cotiza-ahora/artifacts/7vham8yd_ChatGPT%20Image%2013%20may%202026%2C%2019_48_13.png"
              imageAlt="Edificios residenciales en Santiago"
            />
            <ProductCard
              kicker="Para personas"
              title="Residentes"
              icon={HomeIcon}
              accent="terracotta"
              items={SEGUROS_PERSONAS}
              testid="card-personas"
              image="https://customer-assets.emergentagent.com/job_cotiza-ahora/artifacts/9dtgkuqp_WhatsApp%20Image%202026-05-13%20at%2014.45.02.jpeg"
              imageAlt="Familia residente en su hogar"
            />
          </div>
        </div>
      </section>

      {/* FINAL CTA — refined */}
      <section className="py-12 md:py-20">
        <div className="container-x">
          <div className="relative overflow-hidden rounded-2xl md:rounded-3xl py-14 md:py-20 px-6 md:px-8 text-center grain" style={{ background: "linear-gradient(135deg, var(--brand-navy) 0%, var(--brand-navy-2) 100%)", color: "white" }}>
            <div aria-hidden className="absolute -top-32 -right-32 w-[520px] h-[520px] rounded-full opacity-[0.18] blur-3xl" style={{ background: "var(--brand-accent)" }}/>

            <div className="relative">
              <div className="text-[11px] uppercase tracking-[0.24em] font-bold mb-4" style={{ color: "var(--brand-accent-light)" }}>
                Hablemos
              </div>
              <h2 className="text-[40px] sm:text-5xl md:text-7xl lg:text-[88px] font-extrabold tracking-tight leading-[1.0] md:leading-[0.96]" style={{ fontFamily: "Manrope", color: "white" }}>
                ¿Conversamos sobre<br/>
                <span className="serif" style={{ color: "var(--brand-accent-light)", fontSize: "1.02em" }}>tu comunidad?</span>
              </h2>
              <p className="mt-5 md:mt-6 max-w-md mx-auto opacity-80 text-base md:text-lg">
                Escríbenos y armemos juntos una propuesta a la medida de tu comunidad.
              </p>
              <div className="mt-8 md:mt-10 flex flex-col sm:flex-row flex-wrap gap-4 justify-center items-center">
                <a href="#solicitud-cotizacion" className="btn-accent w-full sm:w-auto justify-center" data-testid="final-cta-contacto">
                  Formulario de contacto <ArrowUpRight size={16}/>
                </a>
                <a href="https://wa.me/56942964199" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2.5 text-[15px] font-semibold opacity-90 hover:opacity-100 px-5 py-3 transition" data-testid="final-cta-whatsapp">
                  <WhatsAppIcon size={26} color="#25D366"/> o por Whatsapp
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}

// ====== Refined product card ======
function ProductCard({ kicker, title, icon: Icon, accent, items, testid, image, imageAlt }) {
  const isAccent = accent === "terracotta";
  const iconBg = isAccent ? "var(--brand-accent)" : "var(--brand-navy)";
  const itemBg = isAccent ? "rgba(201, 102, 63, 0.08)" : "rgba(10, 31, 54, 0.05)";
  const itemColor = isAccent ? "var(--brand-accent)" : "var(--brand-navy)";

  return (
    <div className="rounded-2xl bg-white border overflow-hidden transition-all duration-300 hover:shadow-lg" style={{ borderColor: "var(--border-subtle)", boxShadow: "var(--shadow-soft)" }} data-testid={testid}>
      {image && (
        <div className="relative h-44 md:h-52 overflow-hidden">
          <img src={image} alt={imageAlt} className="w-full h-full object-cover transition-transform duration-700 hover:scale-105" draggable={false}/>
        </div>
      )}
      <div className="p-6 md:p-10">
        <div className="flex items-center gap-3.5 mb-6 md:mb-8 pb-5 md:pb-7 border-b" style={{ borderColor: "var(--border-subtle)" }}>
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: iconBg }}>
            <Icon size={20} color="white" strokeWidth={1.75}/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold" style={{ color: "var(--text-muted)" }}>{kicker}</div>
            <h3 className="text-xl md:text-[22px] font-extrabold tracking-tight" style={{ fontFamily: "Manrope" }}>{title}</h3>
          </div>
        </div>
        <ul className="space-y-0">
          {items.map((s, i) => (
            <li
              key={i}
              className="flex items-center gap-4 py-3.5 group cursor-default"
              style={{
                borderBottom: i < items.length - 1 ? "1px solid var(--border-subtle)" : "none",
              }}
              data-testid={`${testid}-item-${i}`}
            >
              <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110" style={{ background: itemBg }}>
                <s.icon size={15} style={{ color: itemColor }} strokeWidth={1.75}/>
              </div>
              <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-0.5 sm:gap-3">
                <span className="font-bold text-[15.5px]" style={{ fontFamily: "Manrope" }}>{s.name}</span>
                <span className="text-[11px] sm:text-[12px] flex-shrink-0" style={{ color: "var(--text-muted)" }}>{s.tag}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}



// Color oficial fijo del banner de todos los afiliados cobrandeados.
// Se mantiene idéntico al que usa Ñuñoa para preservar identidad de marca.
const BRAND_BANNER_BG = "#1E3FAE";
const BRAND_BANNER_COLOR = "#FFFFFF";

function PartnerBanner() {
  const { brand } = useBranding();
  if (!brand || !brand.cobrandeado) return null;
  const logoDark = brand.logoDark;
  const banner = brand.banner || {};
  const title = (banner.text || "").trim();
  const subtext = (banner.subtext || "").trim();
  const titleBold = banner.titleBold !== false; // default true
  const subtextBold = !!banner.subtextBold; // default false
  // El banner se muestra si hay al menos logo, título o subtexto.
  if (!logoDark && !title && !subtext) return null;
  return (
    <div className="w-full pt-4 md:pt-5 pb-1 md:pb-2" style={{ background: "var(--bg-soft)" }} data-testid="partner-banner-wrapper">
      <div className="container-x">
        <div
          className="w-full rounded-xl md:rounded-2xl px-5 sm:px-8 py-4 sm:py-5 flex items-center justify-center gap-4 sm:gap-6"
          style={{ background: BRAND_BANNER_BG, color: BRAND_BANNER_COLOR, boxShadow: "0 6px 22px rgba(30,63,174,0.18)" }}
          data-testid="partner-banner"
        >
          {logoDark && (
            <img
              src={logoDark}
              alt={brand.name}
              className="h-12 sm:h-14 object-contain flex-shrink-0 rounded"
              data-testid="partner-logo-banner"
            />
          )}
          {(title || subtext) && (
            <div className="text-center sm:text-left">
              {title && (
                <p
                  className={`text-[14px] sm:text-[17px] leading-tight ${titleBold ? "font-extrabold" : "font-normal"}`}
                  style={{ fontFamily: "Manrope", letterSpacing: "-0.01em" }}
                  data-testid="partner-banner-title"
                >
                  {title}
                </p>
              )}
              {subtext && (
                <p
                  className={`text-[12px] sm:text-[13.5px] mt-1 opacity-90 leading-tight ${subtextBold ? "font-extrabold" : "font-normal"}`}
                  style={{ fontFamily: "Manrope" }}
                  data-testid="partner-banner-subtext"
                >
                  {subtext}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
